import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import Button from "@/components/ui/button/Button";
import roles from "@/assets/roles.json";
import {
  ChevronLeft,
  User,
  Calendar,
  Clock,
  Hash,
  Phone,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { apiFetch } from "@/service/api";

interface SesionLogin {
  id: number;
  accion_realizada: string;
  fecha: string;
  tipo: string;
  empleado_id: {
    id: number;
    numero_identificacion?: string;
    nombres: string;
    apellidos: string;
    correo_electronico: string;
    telefono?: string;
    rol?: number;
  };
}

export default function VerSesionLogin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sesion, setSesion] = useState<SesionLogin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSesion = async () => {
      try {
        const response = await apiFetch(`acciones-historial/find-one/${id}`);
        if (response.ok) {
          const data = await response.json();
          setSesion(data);
        }
      } catch (error) {
        console.error("Error fetching session detail:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSesion();
  }, [id]);

  const getFecha = (fecha: string) => fecha?.split(" ")[0] || "N/A";
  const getHora = (fecha: string) => fecha?.split(" ")[1] || "N/A";

  const InfoCard = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value?: string;
  }) => (
    <div className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-white/[0.02]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-brand-500 shadow-sm dark:bg-gray-900">
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
          {value || "No especificado"}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (!sesion) {
    return (
      <div className="text-center py-20">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
          Sesion no encontrada
        </h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/seguridad_accesos/sesiones")}
        >
          Volver al listado
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageBreadcrumb pageTitle="Detalle de Sesion" />
      <div className="mb-6">
        <button
          onClick={() => navigate("/seguridad_accesos/sesiones")}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="size-5" />
          Volver al historial
        </button>
      </div>
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03] xl:p-10">
        <div className="flex flex-col items-center mb-10 text-center">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/10">
            <User className="size-10" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white/90">
            {sesion.empleado_id.nombres} {sesion.empleado_id.apellidos}
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            {sesion.empleado_id.correo_electronico}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <InfoCard
            icon={<Calendar className="size-5" />}
            label="Fecha"
            value={getFecha(sesion.fecha)}
          />
          <InfoCard
            icon={<Clock className="size-5" />}
            label="Hora"
            value={getHora(sesion.fecha)}
          />
          <InfoCard
            icon={<Activity className="size-5" />}
            label="Accion Realizada"
            value={sesion.accion_realizada}
          />
          <InfoCard
            icon={<ShieldCheck className="size-5" />}
            label="Tipo de Accion"
            value={sesion.tipo}
          />
        </div>
        <div className="mt-10 pt-10 border-t border-gray-100 dark:border-gray-800">
          <h4 className="mb-6 text-lg font-semibold text-gray-800 dark:text-white/90">
            Informacion del Usuario
          </h4>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <InfoCard
              icon={<Hash className="size-5" />}
              label="Identificacion"
              value={sesion.empleado_id.numero_identificacion}
            />
            <InfoCard
              icon={<Phone className="size-5" />}
              label="Telefono"
              value={sesion.empleado_id.telefono}
            />
            <InfoCard
              icon={<ShieldCheck className="size-5" />}
              label="Rol"
              value={
                roles.find((role) => role.id === sesion.empleado_id.rol)
                  ?.name || "No especificado"
              }
            />
            <InfoCard
              icon={<Activity className="size-5" />}
              label="ID Sesion"
              value={String(sesion.id)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
